import { Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { effortHoursAndDays, effortStepTargets } from "@/lib/effort";
import { cn } from "@/lib/utils";

interface EffortStepperProps {
  /** In hours, one of the steps; null while to be estimated. */
  value: number | null;
  onChange: (effort: number | null) => void;
  /** An idea goes from 2 hours down to "Nog te schatten"; anything else stops at 2 hours. */
  allowNone: boolean;
  /** The stepper's name for screen readers: "Inspanning". */
  label: string;
  /** Takes the focus when it shows: the form opens on the effort. */
  autoFocus?: boolean;
}

/** One of the stepper's two square buttons. */
const STEP_BUTTON =
  "focus-visible:ring-ring ring-offset-background text-foreground hover:bg-accent-light hover:text-primary inline-flex size-8 shrink-0 items-center justify-center rounded-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:hover:bg-transparent aria-disabled:hover:text-foreground pointer-coarse:size-10";

/**
 * An item's effort as one control: "−", the value in hours and days ("12 u
 * (1,5 dag)"), "+". Each press moves one step (2 u, 4 u, 1 dag, then half days
 * up to 5 dagen; `effortStepTargets`). The value is announced as it changes.
 * At either end the button stays focusable but does nothing (`aria-disabled`),
 * so the keyboard never drops to the page's body.
 */
export function EffortStepper({
  value,
  onChange,
  allowNone,
  label,
  autoFocus = false,
}: EffortStepperProps) {
  const { t } = useTranslation();
  const { down, up } = effortStepTargets(value, allowNone);
  const step = (target: number | null | undefined) => {
    if (target !== undefined) {
      onChange(target);
    }
  };
  return (
    <div
      role="group"
      aria-label={label}
      className="border-input bg-card inline-flex items-center rounded-md border p-0.5"
      data-effort-stepper=""
    >
      <button
        type="button"
        aria-label={t("roadmap.effort.less")}
        aria-disabled={down === undefined || undefined}
        onClick={() => step(down)}
        className={STEP_BUTTON}
        data-step="down"
      >
        <Minus aria-hidden="true" className="size-4" />
      </button>
      <output
        aria-live="polite"
        aria-atomic="true"
        className={cn(
          "min-w-36 px-2 text-center text-sm font-medium tabular-nums",
          value === null && "text-muted font-normal",
        )}
        data-effort={value ?? ""}
      >
        {effortHoursAndDays(value)}
      </output>
      <button
        type="button"
        aria-label={t("roadmap.effort.more")}
        aria-disabled={up === undefined || undefined}
        onClick={() => step(up)}
        className={STEP_BUTTON}
        // The form opens on the effort: the keyboard starts on "+".
        autoFocus={autoFocus}
        data-step="up"
      >
        <Plus aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
